/* eslint-disable no-unused-vars */
/**
 * FormsAdminDialogs — "Forms & Types Management" dialog + "Form Type" editor
 * dialog, extracted verbatim from App.js (V2 Forms migration, Jun 2026).
 * ctx pattern (same approach as InsuranceAdminDialogs): the host supplies all
 * state + handlers so the legacy Dashboard and /v2/admin/forms render the
 * exact same UI.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Settings, Edit, Copy, Upload, FileJson, Trash2, Archive, ArchiveRestore, GripVertical, X, FileText, RotateCcw, Download,
  CheckCircle, ClipboardPaste, Search,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { DebouncedInput, DebouncedTextarea } from '../ui/debounced-textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const FormsManagementDialog = ({ ctx }) => {
  const {
    showFormsManagementDialog, setShowFormsManagementDialog,
    formsManagementTab, setFormsManagementTab,
    formTemplates, formTypes,
    fetchFormTemplates,
    setFormTemplates, setFormTypes,
    openFormBuilder,
    duplicateFormTemplate,
    handleDeleteFormTemplate,
    setEditingFormType, setFormTypeFormData, setShowManageFormTypes,
    handleDeleteFormType,
    handleMoveFormTypeUp, handleMoveFormTypeDown,
    headerExtras,
  } = ctx;
  // Local list-UI state (search / sort / filter / pagination) — was App state
  const [formTemplateSearchQuery, setFormTemplateSearchQuery] = useState('');
  const [formTemplateCurrentPage, setFormTemplateCurrentPage] = useState(1);
  const [formTemplateSortBy, setFormTemplateSortBy] = useState('name');
  const [formTemplateFilterType, setFormTemplateFilterType] = useState('all');
  const [formTemplateItemsPerPage, setFormTemplateItemsPerPage] = useState(10);
  const [showArchivedTemplates, setShowArchivedTemplates] = useState(false);
  return (
    <>
      {/* Forms & Types Management Dialog */}
      <Dialog open={showFormsManagementDialog} onOpenChange={setShowFormsManagementDialog}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader className="flex flex-row items-start justify-between">
            <DialogTitle>Forms & Types Management</DialogTitle>
            {headerExtras || null}
          </DialogHeader>
          
          <Tabs value={formsManagementTab} onValueChange={setFormsManagementTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="templates">Form Templates ({formTemplates.filter(t => t.is_active !== false).length})</TabsTrigger>
              <TabsTrigger value="types">Form Types ({formTypes.length})</TabsTrigger>
            </TabsList>
            
            {/* Form Templates Tab */}
            <TabsContent value="templates" className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <p className="text-sm text-gray-600">Create and manage clinical documentation templates</p>
                <div className="flex gap-2 flex-wrap">
                  {/* Hidden file input for batch upload */}
                  <input
                    type="file"
                    id="batch-import-file"
                    accept=".docx,.json,.txt"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      
                      const formData = new FormData();
                      formData.append('file', file);
                      
                      toast.info(`Importing forms from ${file.name}...`);
                      
                      try {
                        const response = await axios.post(`${API}/form-templates/import-batch`, formData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        
                        const { imported_count, error_count, results, errors } = response.data;
                        
                        if (imported_count > 0) {
                          toast.success(`Successfully imported ${imported_count} form(s)!`);
                          if (results && results.length > 0) {
                            void 0 && console.log('Imported forms:', results);
                          }
                        }
                        
                        if (error_count > 0) {
                          toast.warning(`${error_count} form(s) failed to import`);
                          console.error('Import errors:', errors);
                        }
                        
                        fetchFormTemplates(true); // Skip cache after import
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to import forms');
                        console.error('Batch import error:', error);
                      }
                      
                      // Reset input
                      e.target.value = '';
                    }}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById('batch-import-file')?.click()}
                    title="Upload .docx, .json, or .txt file with multiple forms"
                  >
                    <Upload className="h-4 w-4 mr-1" />
                    Import File
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Create a file input to load native JSON file
                      const fileInput = document.createElement('input');
                      fileInput.type = 'file';
                      fileInput.accept = '.json,application/json';
                      fileInput.onchange = async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        
                        try {
                          const text = await file.text();
                          const jsonData = JSON.parse(text);
                          
                          const response = await axios.post(`${API}/form-templates/import-native`, {
                            template_json: jsonData
                          });
                          toast.success(`Form "${response.data.name}" imported with ${response.data.field_count} fields!`);
                          fetchFormTemplates(true);
                        } catch (error) {
                          if (error instanceof SyntaxError) {
                            toast.error(`Invalid JSON file: ${error.message}`);
                          } else {
                            toast.error(error.response?.data?.detail || 'Failed to import form');
                          }
                          console.error('Import error:', error);
                        }
                      };
                      fileInput.click();
                    }}
                    title="Import CareVault native JSON format"
                  >
                    <FileJson className="h-4 w-4 mr-1" />
                    Import Native JSON
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const jsonInput = prompt('Paste your external form JSON here:');
                      if (jsonInput) {
                        const name = prompt('Enter a name for this form template:', 'Imported Form');
                        if (name) {
                          axios.post(`${API}/form-templates/import-external`, {
                            form_json: jsonInput,
                            name: name,
                            description: 'Imported from external system',
                            form_type: 'clinical_note'
                          }).then(response => {
                            toast.success(`Form "${name}" imported with ${response.data.field_count} fields!`);
                            fetchFormTemplates(true); // Skip cache after import
                          }).catch(error => {
                            toast.error(error.response?.data?.detail || 'Failed to import form');
                            console.error('Import error:', error);
                          });
                        }
                      }
                    }}
                  >
                    <ClipboardPaste className="h-4 w-4 mr-1" />
                    Paste JSON
                  </Button>
                  <Button
                    size="sm"
                    className="bg-teal-600 hover:bg-teal-700"
                    onClick={() => {
                      openFormBuilder(null);
                      setShowFormsManagementDialog(false);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Form Template
                  </Button>
                </div>
              </div>
              
              {/* Search Bar */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <DebouncedInput
                  placeholder="Search form templates..."
                  value={formTemplateSearchQuery}
                  onChange={(e) => {
                    setFormTemplateSearchQuery(e.target.value);
                    setFormTemplateCurrentPage(1); // Reset to first page on search
                  }}
                  className="pl-10"
                />
                {formTemplateSearchQuery && (
                  <button
                    onClick={() => {
                      setFormTemplateSearchQuery('');
                      setFormTemplateCurrentPage(1);
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </button>
                )}
              </div>
              
              {/* Sorting, Filtering, and Pagination Controls */}
              <div className="flex flex-wrap items-center gap-3 p-3 bg-gray-50 rounded-lg border">
                {/* Sort By */}
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-600">Sort:</label>
                  <select
                    value={formTemplateSortBy}
                    onChange={(e) => setFormTemplateSortBy(e.target.value)}
                    className="text-sm border rounded px-2 py-1.5 bg-white"
                  >
                    <option value="alphabetical">A-Z (Alphabetical)</option>
                    <option value="alphabetical_desc">Z-A (Reverse)</option>
                    <option value="form_type">By Form Type</option>
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                  </select>
                </div>
                
                {/* Filter by Form Type */}
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-600">Type:</label>
                  <select
                    value={formTemplateFilterType}
                    onChange={(e) => {
                      setFormTemplateFilterType(e.target.value);
                      setFormTemplateCurrentPage(1); // Reset to first page on filter change
                    }}
                    className="text-sm border rounded px-2 py-1.5 bg-white min-w-[140px]"
                  >
                    <option value="all">All Types</option>
                    <option value="no_type">No Type Assigned</option>
                    {[...formTypes].sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999) || a.name.localeCompare(b.name)).map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>
                
                {/* Items Per Page */}
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-600">Show:</label>
                  <select
                    value={formTemplateItemsPerPage}
                    onChange={(e) => {
                      setFormTemplateItemsPerPage(Number(e.target.value));
                      setFormTemplateCurrentPage(1); // Reset to first page
                    }}
                    className="text-sm border rounded px-2 py-1.5 bg-white"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>All</option>
                  </select>
                </div>
                
                {/* Show Archived Toggle - Changed to dropdown */}
                <div className="flex items-center gap-2 ml-auto">
                  <label className="text-sm font-medium text-gray-600">Status:</label>
                  <select
                    value={showArchivedTemplates === 'archived' ? 'archived' : showArchivedTemplates === true || showArchivedTemplates === 'all' ? 'all' : 'active'}
                    onChange={(e) => {
                      setShowArchivedTemplates(e.target.value);
                      setFormTemplateCurrentPage(1);
                    }}
                    className="text-sm border rounded px-2 py-1.5 bg-white"
                  >
                    <option value="active">Active Only</option>
                    <option value="archived">Archived Only</option>
                    <option value="all">All</option>
                  </select>
                </div>
              </div>
              
              <div className="space-y-2 max-h-[45vh] overflow-y-auto">
                {(() => {
                  // Step 1: Filter templates
                  let filteredTemplates = formTemplates.filter(template => {
                    // Filter by archived status
                    if (showArchivedTemplates === 'active' && template.is_active === false) return false;
                    if (showArchivedTemplates === 'archived' && template.is_active !== false) return false;
                    // 'all' shows everything
                    
                    // Filter by form type
                    if (formTemplateFilterType !== 'all') {
                      if (formTemplateFilterType === 'no_type') {
                        if (template.form_type) return false;
                      } else if (template.form_type !== formTemplateFilterType) {
                        return false;
                      }
                    }
                    
                    // Filter by search query
                    if (!formTemplateSearchQuery.trim()) return true;
                    const query = formTemplateSearchQuery.toLowerCase();
                    const typeName = formTypes.find(t => t.id === template.form_type)?.name || '';
                    return (
                      template.name?.toLowerCase().includes(query) ||
                      template.description?.toLowerCase().includes(query) ||
                      typeName.toLowerCase().includes(query)
                    );
                  });
                  
                  // Step 2: Sort templates
                  // Priority: 1) Progress Notes type, 2) No Show forms, 3) Everything else alphabetically
                  filteredTemplates = [...filteredTemplates].sort((a, b) => {
                    // Helper to get form type name
                    const getTypeName = (template) => formTypes.find(t => t.id === template.form_type)?.name || '';
                    
                    // Helper to check if it's a Progress Notes type
                    const isProgressNoteType = (template) => {
                      const typeName = getTypeName(template).toLowerCase();
                      return typeName.includes('progress note');
                    };
                    
                    // Helper to check if it's a No Show form
                    const isNoShowForm = (template) => {
                      const name = (template.name || '').toLowerCase();
                      return name.includes('no show') || name.includes('no-show') || name.includes('cancellation');
                    };
                    
                    const aIsProgress = isProgressNoteType(a);
                    const bIsProgress = isProgressNoteType(b);
                    const aIsNoShow = isNoShowForm(a);
                    const bIsNoShow = isNoShowForm(b);
                    
                    // Progress Notes type comes first
                    if (aIsProgress && !bIsProgress) return -1;
                    if (!aIsProgress && bIsProgress) return 1;
                    
                    // Within Progress Notes, sort alphabetically
                    if (aIsProgress && bIsProgress) {
                      return (a.name || '').localeCompare(b.name || '');
                    }
                    
                    // No Show forms come after Progress Notes but before others
                    if (aIsNoShow && !bIsNoShow) return -1;
                    if (!aIsNoShow && bIsNoShow) return 1;
                    
                    // Apply user's selected sort for everything else
                    switch (formTemplateSortBy) {
                      case 'alphabetical':
                        return (a.name || '').localeCompare(b.name || '');
                      case 'alphabetical_desc':
                        return (b.name || '').localeCompare(a.name || '');
                      case 'form_type':
                        const typeA = getTypeName(a) || 'zzz';
                        const typeB = getTypeName(b) || 'zzz';
                        const typeCompare = typeA.localeCompare(typeB);
                        if (typeCompare !== 0) return typeCompare;
                        return (a.name || '').localeCompare(b.name || '');
                      case 'newest':
                        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
                      case 'oldest':
                        return new Date(a.created_at || 0) - new Date(b.created_at || 0);
                      default:
                        return (a.name || '').localeCompare(b.name || '');
                    }
                  });
                  
                  const totalCount = filteredTemplates.length;
                  
                  // Step 3: Paginate (unless showing all)
                  const totalPages = formTemplateItemsPerPage >= 100 ? 1 : Math.ceil(totalCount / formTemplateItemsPerPage);
                  const startIndex = formTemplateItemsPerPage >= 100 ? 0 : (formTemplateCurrentPage - 1) * formTemplateItemsPerPage;
                  const paginatedTemplates = formTemplateItemsPerPage >= 100 
                    ? filteredTemplates 
                    : filteredTemplates.slice(startIndex, startIndex + formTemplateItemsPerPage);
                  
                  if (filteredTemplates.length === 0) {
                    return (
                      <p className="text-sm text-gray-500 text-center py-8">
                        {formTemplateSearchQuery ? `No templates matching "${formTemplateSearchQuery}"` : 'No form templates yet.'}
                      </p>
                    );
                  }
                  
                  return (
                    <>
                      {/* Results summary */}
                      <div className="text-sm text-gray-500 mb-2 flex items-center justify-between">
                        <span>
                          Showing {startIndex + 1}-{Math.min(startIndex + paginatedTemplates.length, totalCount)} of {totalCount} templates
                        </span>
                        {totalPages > 1 && (
                          <span className="text-xs text-gray-400">
                            Page {formTemplateCurrentPage} of {totalPages}
                          </span>
                        )}
                      </div>
                      
                      {paginatedTemplates.map((template) => (
                    <div key={template.id} className={`flex items-center justify-between p-3 rounded border ${template.is_active === false ? 'bg-gray-100 border-gray-300' : 'bg-gray-50'}`}>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{template.name}</span>
                          {template.is_system && (
                            <Badge className="text-xs bg-teal-100 text-teal-800 border-teal-300">System Form</Badge>
                          )}
                          {!template.is_active && (
                            <Badge variant="outline" className="text-xs bg-gray-200">Archived</Badge>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">
                          {template.form_type ? `Type: ${formTypes.find(t => t.id === template.form_type)?.name || template.form_type}` : 'No type'} • {template.form_fields?.length || 0} fields
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          title="Edit"
                          onClick={() => {
                            openFormBuilder(template);
                            setShowFormsManagementDialog(false);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        {template.is_active !== false ? (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="Duplicate"
                              onClick={() => duplicateFormTemplate(template)}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            {!template.is_system && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                              title="Archive"
                              onClick={() => handleDeleteFormTemplate(template.id)}
                            >
                              <Archive className="h-4 w-4" />
                            </Button>
                            )}
                          </>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              title="Restore"
                              onClick={async () => {
                                try {
                                  await axios.post(`${API}/form-templates/${template.id}/restore`, {}, {
                                    headers: { 'Authorization': `Bearer ${sessionStorage.getItem('token')}` }
                                  });
                                  toast.success("Form template restored successfully");
                                  fetchFormTemplates();
                                } catch (error) {
                                  console.error('Error restoring template:', error);
                                  toast.error("Failed to restore form template");
                                }
                              }}
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Delete Forever"
                              onClick={async () => {
                                if (!window.confirm("Are you sure you want to PERMANENTLY delete this template? This cannot be undone.")) {
                                  return;
                                }
                                try {
                                  await axios.delete(`${API}/form-templates/${template.id}/permanent`, {
                                    headers: { 'Authorization': `Bearer ${sessionStorage.getItem('token')}` }
                                  });
                                  toast.success("Form template permanently deleted");
                                  fetchFormTemplates();
                                } catch (error) {
                                  console.error('Error permanently deleting template:', error);
                                  toast.error(error.response?.data?.detail || "Failed to permanently delete form template");
                                }
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                      
                      {/* Pagination Controls */}
                      {totalPages > 1 && (
                        <div className="flex items-center justify-center gap-2 pt-4 border-t mt-4">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={formTemplateCurrentPage === 1}
                            onClick={() => setFormTemplateCurrentPage(1)}
                          >
                            First
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={formTemplateCurrentPage === 1}
                            onClick={() => setFormTemplateCurrentPage(p => Math.max(1, p - 1))}
                          >
                            Previous
                          </Button>
                          <span className="px-4 py-1 text-sm">
                            Page {formTemplateCurrentPage} of {totalPages}
                          </span>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={formTemplateCurrentPage === totalPages}
                            onClick={() => setFormTemplateCurrentPage(p => Math.min(totalPages, p + 1))}
                          >
                            Next
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={formTemplateCurrentPage === totalPages}
                            onClick={() => setFormTemplateCurrentPage(totalPages)}
                          >
                            Last
                          </Button>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </TabsContent>
            
            {/* Form Types Tab */}
            <TabsContent value="types" className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">Organize forms into categories. Use arrows to reorder.</p>
                <Button
                  size="sm"
                  className="bg-teal-600 hover:bg-teal-700"
                  onClick={() => {
                    setEditingFormType(null);
                    setFormTypeFormData({ name: '', description: '', sort_order: formTypes.length + 1 });
                    setShowManageFormTypes(true);
                  }}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Form Type
                </Button>
              </div>
              
              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                {formTypes.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-8">No form types yet.</p>
                ) : (
                  formTypes.map((type, index) => (
                    <div key={type.id} className="flex items-center justify-between p-3 bg-gray-50 rounded border">
                      <div className="flex items-center space-x-3 flex-1">
                        <span className="text-sm font-bold text-gray-600 w-8">{type.sort_order}</span>
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            {type.name}
                            {type.is_system && (
                              <Badge className="bg-blue-100 text-blue-700 border border-blue-200 text-[10px]" data-testid={`form-type-system-badge-${type.id}`}>
                                System
                              </Badge>
                            )}
                            {type.is_system && type.files_under && type.files_under !== type.name && (
                              <span className="text-[11px] text-gray-500">files under “{type.files_under}”</span>
                            )}
                          </div>
                          {type.description && (
                            <div className="text-xs text-gray-500">{type.description}</div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleMoveFormTypeUp(type.id)}
                          disabled={index === 0}
                          title="Move Up"
                        >
                          ↑
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleMoveFormTypeDown(type.id)}
                          disabled={index === formTypes.length - 1}
                          title="Move Down"
                        >
                          ↓
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingFormType(type);
                            setFormTypeFormData({
                              name: type.name,
                              description: type.description || '',
                              sort_order: type.sort_order,
                              files_under: type.files_under || type.name,
                            });
                            setShowManageFormTypes(true);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        {!type.is_system && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600"
                          onClick={() => {
                            if (window.confirm(`Delete "${type.name}"? This won't affect existing forms.`)) {
                              handleDeleteFormType(type.id);
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

    </>
  );
};

export const FormTypeDialog = ({ ctx }) => {
  const {
    showManageFormTypes, setShowManageFormTypes,
    editingFormType, setEditingFormType,
    formTypeFormData, setFormTypeFormData,
    handleCreateFormType,
    formTypes = [],
  } = ctx;
  const isSystem = !!editingFormType?.is_system;
  return (
    <>
      {/* Form Type Management Dialog */}
      <Dialog open={showManageFormTypes} onOpenChange={setShowManageFormTypes}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingFormType ? 'Edit Form Type' : 'Add Form Type'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateFormType} className="space-y-4">
            <div>
              <Label htmlFor="formtype-name">Category Name *</Label>
              <DebouncedInput
                id="formtype-name"
                value={formTypeFormData.name}
                onChange={(e) => setFormTypeFormData(prev => ({...prev, name: e.target.value}))}
                placeholder="e.g., Progress Notes, Assessments"
                required
                disabled={isSystem}
              />
              {isSystem && (
                <p className="text-xs text-blue-700 mt-1">System type — the name is locked because automated filing pipelines depend on it.</p>
              )}
            </div>

            <div>
              <Label htmlFor="formtype-description">Description</Label>
              <DebouncedTextarea
                id="formtype-description"
                value={formTypeFormData.description}
                onChange={(e) => setFormTypeFormData(prev => ({...prev, description: e.target.value}))}
                placeholder="Brief description of this form category..."
                rows={3}
              />
            </div>

            {isSystem && (
              <div>
                <Label htmlFor="formtype-files-under">Files under</Label>
                <select
                  id="formtype-files-under"
                  className="w-full h-9 border border-gray-300 rounded-md px-2 text-sm bg-white"
                  value={formTypeFormData.files_under || editingFormType?.name || ''}
                  onChange={(e) => setFormTypeFormData(prev => ({ ...prev, files_under: e.target.value }))}
                  data-testid="formtype-files-under-select"
                >
                  {formTypes.filter(t => t.is_active !== false).map(t => (
                    <option key={t.id} value={t.name}>{t.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Documents of this system type will be filed under the selected chart section.
                </p>
              </div>
            )}
            
            <div>
              <Label htmlFor="formtype-sortorder">Sort Order</Label>
              <Input
                id="formtype-sortorder"
                type="number"
                value={formTypeFormData.sort_order}
                onChange={(e) => setFormTypeFormData(prev => ({...prev, sort_order: parseInt(e.target.value) || 0}))}
                placeholder="0"
              />
              <p className="text-xs text-gray-500 mt-1">Lower numbers appear first in lists</p>
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => {
                setShowManageFormTypes(false);
                setEditingFormType(null);
                setFormTypeFormData({ name: '', description: '', sort_order: 0 });
              }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                {editingFormType ? 'Update' : 'Create'} Form Type
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

    </>
  );
};
